import { FC } from 'react';

import { IEditorPolicyCondition } from '@entities/routing/config-version';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, Stack, Typography } from '@mui/material';
import { SelectMulti } from '@shared/components/Form';

import { toOptions } from './selectOptions';

export type TConditionDimension = keyof IEditorPolicyCondition;

// The GET /router/settings dictionaries the four selects read from (code → label).
export type TConditionDictionaries = Partial<Record<TConditionDimension, Record<string, string>>>;

interface IProps {
  index: number;
  condition: IEditorPolicyCondition;
  dictionaries?: TConditionDictionaries;
  canRemove: boolean;
  onChange: (dimension: TConditionDimension, values: string[]) => void;
  onRemove: () => void;
}

// An empty dimension matches everything; the placeholder says so.
const DIMENSIONS: { key: TConditionDimension; label: string; placeholder: string }[] = [
  { key: 'currency', label: 'Currencies', placeholder: 'Any currency' },
  { key: 'method', label: 'Methods', placeholder: 'Any method' },
  { key: 'traffic', label: 'Traffic', placeholder: 'Any traffic' },
  { key: 'merchants', label: 'Merchants', placeholder: 'Any merchant' },
];

// One "Condition #n" group of the policy form: the Policies page's panel of four selects.
const PolicyConditionGroup: FC<IProps> = ({
  index,
  condition,
  dictionaries,
  canRemove,
  onChange,
  onRemove,
}) => (
  <Stack
    spacing={2}
    sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}
    data-testid='condition-group'
  >
    <Stack direction='row' justifyContent='space-between' alignItems='center'>
      <Typography variant='subtitle2'>Condition #{index + 1}</Typography>
      {canRemove && (
        <IconButton
          size='small'
          color='error'
          aria-label={`Remove condition #${index + 1}`}
          onClick={onRemove}
        >
          <DeleteIcon fontSize='small' />
        </IconButton>
      )}
    </Stack>

    {DIMENSIONS.map(({ key: dimension, label, placeholder }) => {
      const selected = condition[dimension];
      const dictionary = dictionaries?.[dimension];

      return (
        <Stack key={dimension} spacing={1}>
          <Typography variant='caption' color='text.secondary'>
            {label}
          </Typography>
          <SelectMulti
            name={`conditions[${index}][${dimension}][]`}
            options={toOptions(dictionary, selected)}
            value={selected.map(value => ({ value, label: dictionary?.[value] ?? value }))}
            placeholder={placeholder}
            onChange={value =>
              onChange(
                dimension,
                value.map(option => option.value),
              )
            }
          />
        </Stack>
      );
    })}
  </Stack>
);

export default PolicyConditionGroup;
